import React from "react";

import { Body } from "src/components/typography/typography";
import { isNotNil } from "src/utils/type_guards";

/*
 * Types
 */

export type ConstructMeta = Record<string, Opportunity>;

export interface ConstructNotesProps {
    readonly metas: ReadonlyArray<ConstructMeta>;
}

/*
 * Helpers
 */

/**
 * A construct's title, marked with a trailing asterisk when the construct carries a footnote (`extra`).
 * The asterisk lives on the title only, so the footnote beneath the scores has a single marker to refer to.
 */
export const constructTitle = (construct: Opportunity): string =>
    isNotNil(construct.extra) ? `${construct.title}*` : construct.title;

/** Every footnote carried by the given construct metas, in the order the metas (and their constructs) are given. */
export const constructNotes = (metas: ReadonlyArray<ConstructMeta>): ReadonlyArray<string> =>
    metas.reduce<ReadonlyArray<string>>(
        (notes, meta) =>
            notes.concat(
                Object.values(meta)
                    .map((construct) => construct.extra)
                    .filter(isNotNil),
            ),
        [],
    );

/*
 * Main
 */

/** The asterisked footnotes for a results list, in the report's small-print style. */
export const ConstructNotes = (props: ConstructNotesProps): React.ReactElement => {
    const { metas } = props;

    return (
        <>
            {constructNotes(metas).map((note) => (
                <Body className="small-domain-text" key={note}>
                    {note}
                </Body>
            ))}
        </>
    );
};
